import { setLoaderStatus } from '@store/features/ui/actions';
import { dispatchToStore } from '@store/accessor';
import axios from 'axios';

export const fetchGraphQLApiData = async (
  query: string,
  action?: { showStatus: boolean; message: string },
) => {
  try {
    const response = await axios.post('/api/graphql', { query });
    // Guard: if the response is not a JSON object (e.g. an HTML page was returned
    // because the session expired before the axios interceptor could catch the 401),
    // throw so the caller gets an error rather than silently swallowing it.
    if (typeof response.data !== 'object' || response.data === null) {
      throw new Error('Unexpected non-JSON response from /api/graphql: session may have expired');
    }
    if (!response?.data) {
      console.error('GraphQL response is undefined or missing data:', response);
      throw new Error('No data in GraphQL response');
    }
    if (Object.keys(response.data).includes('errors')) {
      const messages = response.data.errors.map((e: any) => e.message).join('; ');
      // If data is also present this is a partial-success response (e.g. one list
      // entry had a non-null violation). Log a warning and return the partial data
      // so the caller can filter out nulls rather than showing a full-page error.
      if (response.data.data) {
        console.warn('GraphQL partial errors (data still returned):', messages);
        // Still a success from the caller's side — it got its data — so this branch keeps the
        // toast it has always shown. Only the outright-rejected case loses it.
        action?.showStatus &&
          dispatchToStore(setLoaderStatus({
            status: 'success',
            message: `${action?.message} success`,
          }));
        return response.data;
      }
      console.error('GraphQL returned errors:', response.data.errors);
      throw new Error(`GraphQL errors: ${messages}`);
    }
    /*
     * Announce success only once it is known to be one.
     *
     * This fired immediately after the HTTP call, before the `errors` check below it, so a
     * mutation the server rejected showed "… success" and then "… failed" half a second later.
     * Every caller passing `showStatus` inherited it. It matters more now that a write can be
     * legitimately refused -- a duplicate application -- because the pair reads as the guard being
     * broken rather than as the guard working.
     */
    action?.showStatus &&
      dispatchToStore(setLoaderStatus({
        status: 'success',
        message: `${action?.message} success`,
      }));
    return response.data;
  } catch (error) {
    action?.showStatus &&
      dispatchToStore(setLoaderStatus({
        status: 'error',
        message: `${action?.message} failed`,
      }));
    console.error('Error fetching data:', error);
    throw error; // Re-throw the error so calling code can handle it properly
  }
};
